import { allContractorSubscriptionPlanOptions, contractorStatusOptions } from "constants/SelectFieldOptions"
import SubscriptionPlanTitlesWithPrefix from "constants/SubscriptionPlanTitlesWithPrefix"
import { ApiTypes, UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { parseDateField } from "utils/dateUtils"
import { ContractorStatus } from "components/table/columnComponents/ContractorStatus"
import RowExpandToggle from "components/table/columnComponents/RowExpandToggle"

const getMetricsValue = (
  row: ApiTypes.Model.EnterpriseContractorListRow,
  contractor: ApiTypes.Model.Contractor | undefined,
  key: keyof ApiTypes.Model.EnterpriseContractorListMetrics,
) => {
  const enterpriseValue = row.enterprise?.metrics?.[key] ?? 0
  const contractorValue = contractor?.[key] ?? 0

  if (row.type === "enterprise") return enterpriseValue
  if (row.type === "single") return enterpriseValue ?? contractorValue
  return 0
}

const getSubscriptionPlanValue = (subscriptionPlan?: SubscriptionPlan | null | undefined): string | null => {
  return subscriptionPlan && subscriptionPlan in SubscriptionPlanTitlesWithPrefix
    ? SubscriptionPlanTitlesWithPrefix[subscriptionPlan]
    : null
}

const getEnterpriseCells = (
  row: ApiTypes.Model.EnterpriseContractorListRow,
): Omit<UITypes.TableBodyRow, "rowSettings"> => {
  const enterprise = row.enterprise

  return {
    group_toggle: { value: "", hidden: false },
    name: { value: enterprise?.name },
    brand: { value: null },
    type: { value: `Enterprise • ${enterprise?.contractor_count ?? row.contractors?.length ?? 0} linked` },
    account_status_id: { value: null },
    is_enabled: { value: enterprise?.is_enabled ? "1" : "0" },
    last_login: { value: parseDateField(enterprise?.last_login || null) },
    create_date: { value: parseDateField(enterprise?.created_at || null) },
    warranty: { value: null },
    country: { value: enterprise?.country?.name },
    state: { value: enterprise?.state?.name },
    purchased: { value: null },
    installed: { value: null },
    inventory: { value: null },
    install_7_day: { value: null },
    active_mobile: { value: null },
    subscription_plan: { value: getSubscriptionPlanValue(enterprise?.subscription_plan) },
  }
}

const getContractorCells = (
  row: ApiTypes.Model.EnterpriseContractorListRow,
  contractor?: ApiTypes.Model.Contractor,
): Omit<UITypes.TableBodyRow, "rowSettings"> => ({
  group_toggle: { value: "", hidden: true },
  name: { value: contractor?.name },
  brand: { value: contractor?.brand },
  type: { value: "Single" },
  account_status_id: { value: contractor?.account_status ? String(contractor.account_status.id) : "" },
  is_enabled: { value: contractor?.is_enabled ? "1" : "0" },
  last_login: { value: parseDateField(contractor?.last_login || null) },
  create_date: { value: parseDateField(contractor?.created_at || null) },
  warranty: { value: getMetricsValue(row, contractor, "warranty") },
  country: { value: contractor?.country?.name },
  state: { value: contractor?.state?.name },
  purchased: { value: getMetricsValue(row, contractor, "purchased_devices") },
  installed: { value: getMetricsValue(row, contractor, "installed_devices") },
  inventory: { value: getMetricsValue(row, contractor, "inventory_devices") },
  install_7_day: { value: getMetricsValue(row, contractor, "install_7_day") },
  active_mobile: { value: getMetricsValue(row, contractor, "active_mobile") },
  subscription_plan: { value: getSubscriptionPlanValue(contractor?.subscription_plan) },
})

const convertContractorToSubRow = (contractor: ApiTypes.Model.Contractor): UITypes.TableBodyRow =>
  ({
    group_toggle: { value: "", hidden: true },
    name: { value: contractor.name },
    brand: { value: contractor.brand },
    type: { value: "Single" },
    account_status_id: { value: contractor.account_status ? String(contractor.account_status.id) : "" },
    is_enabled: { value: contractor.is_enabled ? "1" : "0" },
    last_login: { value: parseDateField(contractor.last_login) },
    create_date: { value: parseDateField(contractor.created_at) },
    warranty: { value: contractor.warranty },
    country: { value: contractor.country?.name },
    state: { value: contractor.state?.name },
    purchased: { value: contractor.purchased_devices ?? 0 },
    installed: { value: contractor.installed_devices ?? 0 },
    inventory: { value: contractor.inventory_devices ?? 0 },
    install_7_day: { value: contractor.install_7_day ?? 0 },
    active_mobile: { value: contractor.active_mobile ?? 0 },
    subscription_plan: { value: getSubscriptionPlanValue(contractor?.subscription_plan) },
    rowSettings: {
      id: Number(contractor.id),
      item: { type: "contractor", contractor },
      isEnable: !!contractor.is_enabled,
    },
  }) as UITypes.TableBodyRow

export const convertEnterpriseContractorsToTableRows = (
  data?: ApiTypes.Model.EnterpriseContractorListRow[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(row => {
    const contractor = row.contractors?.[0]

    if (row.type === "enterprise") {
      return {
        ...getEnterpriseCells(row),
        subRows: row.contractors?.map(convertContractorToSubRow) || [],
        rowSettings: {
          id: Number(row.enterprise?.id),
          item: row,
          isEnable: !!row.enterprise?.is_enabled,
        },
      } as UITypes.TableBodyRow
    }

    return {
      ...getContractorCells(row, contractor),
      subRows: [],
      rowSettings: {
        id: Number(contractor?.id),
        item: row,
        isEnable: !!contractor?.is_enabled,
      },
    } as UITypes.TableBodyRow
  })
}

export const getDefaultEnterpriseExpandedRows = (data?: ApiTypes.Model.EnterpriseContractorListRow[]) =>
  data?.reduce<Record<string, boolean>>((expandedRows, row) => {
    if (row.type === "enterprise" && row.contractors?.length && row.enterprise?.id !== undefined) {
      expandedRows[String(row.enterprise.id)] = true
    }

    return expandedRows
  }, {}) || {}

export const getEnterpriseContractorTableHeaderCells = (
  contractorAccountStatusOptions: UITypes.Option[],
): UITypes.TableHeaderCell[] => [
  {
    title: "",
    name: "group_toggle",
    cellComponent: RowExpandToggle,
    size: "mini",
  },
  {
    title: "Company name",
    type: "sort-search",
    name: "name",
  },
  {
    title: "Brand Name",
    type: "sort-search",
    name: "brand",
  },
  {
    title: "Type",
    type: "select",
    name: "type",
    options: [
      { label: "Enterprise", value: "enterprise" },
      { label: "Single", value: "single" },
    ],
  },
  {
    title: "Contractor Status",
    type: "color-select",
    name: "account_status_id",
    options: contractorAccountStatusOptions,
    cellComponent: ContractorStatus,
  },
  {
    title: "Account Status",
    type: "select",
    name: "is_enabled",
    options: contractorStatusOptions,
    cellComponent: "boolean",
  },
  {
    title: "Last Login",
    type: "default",
    name: "last_login",
  },
  {
    title: "Creation Date",
    type: "sort-date-range",
    name: "create_date",
  },
  {
    title: "Warranty",
    type: "sort-search-number",
    name: "warranty",
  },
  {
    title: "Country",
    type: "sort-search",
    name: "country",
  },
  {
    title: "State",
    type: "sort-search",
    name: "state",
  },
  {
    title: "Subscription Plan",
    type: "select",
    name: "subscription_plan",
    options: allContractorSubscriptionPlanOptions,
  },
  {
    title: "Purchased Devices",
    type: "sort-search-number",
    name: "purchased",
  },
  {
    title: "Installed Thermostats",
    type: "sort-search-number",
    name: "installed",
  },
  {
    title: "Installed Last 7 days",
    type: "sort",
    name: "install_7_day",
  },
  {
    title: "Activated mobile apps",
    type: "sort",
    name: "active_mobile",
  },
  {
    title: "Inventory",
    type: "sort-search-number",
    name: "inventory",
  },
]
